import Reveal from "./Reveal";
import BookCallButton from "./BookCallButton";
import { Star, Squiggle } from "./doodles";
import { FOUNDER_IMAGE } from "../../config";

// FOUNDER IMAGE — to use a real photo, drop it in /public and set
// FOUNDER_IMAGE in src/config.js (e.g. "/meghana.jpg").
function FounderPortrait() {
  if (FOUNDER_IMAGE) {
    return (
      <img
        src={FOUNDER_IMAGE}
        alt="Meghana, founder of Bello Paper"
        className="h-full w-full object-cover object-[50%_28%]"
        data-testid="founder-photo"
      />
    );
  }
  return (
    <svg
      viewBox="0 0 480 560"
      role="img"
      aria-label="Illustrated placeholder portrait of Meghana, founder of Bello Paper"
      className="h-full w-full"
      data-testid="founder-photo-placeholder"
    >
      <rect width="480" height="560" fill="#8ED6F2" />
      <g fill="#1E2A5A" opacity="0.12">
        {Array.from({ length: 11 }).map((_, r) =>
          Array.from({ length: 10 }).map((_, c) => (
            <circle key={`${r}-${c}`} cx={24 + c * 48} cy={24 + r * 48} r="2.4" />
          ))
        )}
      </g>
      <circle cx="240" cy="300" r="185" fill="#9B8CF2" />
      <circle cx="240" cy="225" r="78" fill="#1E2A5A" />
      <path d="M240 285c-88 0-140 58-148 150 0 14 10 24 24 24h248c14 0 24-10 24-24-8-92-60-150-148-150Z" fill="#FF8FB1" />
      <circle cx="212" cy="215" r="9" fill="#FDFBF7" />
      <circle cx="268" cy="215" r="9" fill="#FDFBF7" />
      <path d="M216 252c14 12 34 12 48 0" stroke="#FDFBF7" strokeWidth="7" strokeLinecap="round" fill="none" />
      <path d="M150 130c20-16 46-16 66 0" stroke="#FCE96A" strokeWidth="8" strokeLinecap="round" fill="none" />
      <path d="M398 96c4 20 15 31 35 35-20 4-31 15-35 35-4-20-15-31-35-35 20-4 31-15 35-35Z" fill="#FCE96A" />
      <path d="M88 420c4 16 12 24 28 28-16 4-24 12-28 28-4-16-12-24-28-28 16-4 24-12 28-28Z" fill="#FDFBF7" />
    </svg>
  );
}

export default function Founder() {
  return (
    <section id="about" data-testid="founder-section" className="pt-8 pb-24 md:pt-12 md:pb-32" aria-label="About the founder">
      <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 md:px-12 lg:grid-cols-[5fr_6fr] lg:gap-20 lg:px-20">
        <Reveal className="order-2 lg:order-1">
          <div className="relative mx-auto max-w-[420px]">
            <div className="rotate-[-2.5deg] overflow-hidden rounded-[2.5rem] border-2 border-navy transition-transform duration-500 hover:rotate-0">
              <div className="aspect-[6/7]">
                <FounderPortrait />
              </div>
            </div>
            <span
              className="absolute -top-4 left-10 h-8 w-24 rotate-[-8deg] rounded-sm bg-sunny/90"
              aria-hidden="true"
            />
            <p className="mt-5 -rotate-2 text-center text-lg font-bold italic text-blush md:text-xl" data-testid="founder-label">
              Founder, Bello Paper ✦
            </p>
          </div>
        </Reveal>

        <div className="order-1 lg:order-2">
          <Reveal>
            <h2
              className="font-display text-[clamp(2.6rem,5.5vw,5rem)] font-semibold leading-[1.02] tracking-tight text-navy"
              data-testid="founder-headline"
            >
              Hi, I&rsquo;m{" "}
              <span className="relative inline-block">
                Meghana.
                <Squiggle className="absolute -bottom-3 left-0 w-full text-grape md:-bottom-4" aria-hidden="true" />
              </span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 space-y-5 text-base font-semibold leading-relaxed text-navy/80 md:text-lg" data-testid="founder-bio">
              <p>
                I battled clinical depression for more than 8 long years. And for the first two, I had no idea my
                symptoms pointed to a mental illness. I refused to seek help.
              </p>
              <p className="space-y-1">
                <em className="block">&ldquo;I am strong enough.&rdquo;</em>
                <em className="block">&ldquo;What will people think?&rdquo;</em>
                <em className="block">&ldquo;I just need to work harder.&rdquo;</em>
              </p>
              <p>
                Until I finally gave in and booked a therapist&rsquo;s appointment. Honestly, that has been one of
                the bravest things I&rsquo;ve ever done for myself. After years of navigating the highest highs and
                the lowest lows, I took a career break to focus on my mental health.
              </p>
              <p>
                Here&rsquo;s the other thing about me: <strong>I&rsquo;ve always loved paper</strong>. My friends
                still remember my neat, colourful notes from school. And now, after every therapy session, I use it
                to understand my thoughts and reflect on what I&rsquo;ve learned.
              </p>
              <p>
                <em className="block">And then I thought, hold on.</em>
                <strong className="block">
                  What if paper could help us talk about the difficult things? Break taboos? Enable conversations
                  through play?
                </strong>
              </p>
              <p>
                <strong>That&rsquo;s how Bello Paper was born.</strong> My love for paper meets my hope of creating
                impactful products that help people reconnect, start conversations, reflect, unwind and play.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-9">
              <BookCallButton variant="big" tone="blush" testId="founder-cta">
                Book a call with me
              </BookCallButton>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
